import { useCallback, useEffect, useRef } from 'react';

const readyOf = (fxRef) => (fxRef.current?.status === 'ready' ? fxRef.current : null);

/**
 * The entries that wear the locked (#1) look: the locked up-next song and the
 * song at #1. Neither can be voted or boosted (ENG-805/807).
 */
export const lockedIdsOf = (entries = []) =>
  entries.filter((e) => e && (e.locked || e.position === 1)).map((e) => e.entryId);

/**
 * Afterglow and the #1 lock on the stream page's queue (ENG-809, FX-4).
 *
 * - The afterglow is the viewer's own vibe set. The engine starts it on
 *   `boost-end` with the boost's tier and color, and fades it in over the
 *   rules' `handoffOutMs` (400 ms); a cancelled boost has none. It draws
 *   with the stage's vibe, so Reduce effects (ENG-810, `REDUCED_VIBE`:
 *   no afterglow flicker or lightning) applies to it.
 * - Lock: every card shown at #1 or as the locked up-next song is locked
 *   (`card.setLocked(true)`, which turns its afterglow the #1 color); every
 *   other card is unlocked. It follows `entries`, the order the rail shows
 *   (held while a boost plays, `useFxBoosts`), so a boost to the top takes
 *   the look when its move lands it at #1, and a song pushed off #1 loses it.
 *   The set lives on the stage (`fx.setLockedIds`), so cards created later
 *   start in the right state.
 * - `onSongStarted` takes the public `song_started` frame ({entryId}): the
 *   song's card loses its afterglow, its build and its booster's look
 *   (`card.clearAfterglow()`, `card.clearBuild()`, `fx.clearCardVibe`). The
 *   server stops sending its energy, since only waiting songs have backing.
 * - The booster's look (ENG-866): a boosted song shows the vibe set of whoever
 *   boosted it last (`boostedVibeId` on its entry) until it plays, for every
 *   viewer, after a reload or a late join too. It follows `entries` through
 *   `fx.setCardVibes`; the afterglow and the #1 color then draw with it.
 */
export const useFxAfterglow = ({ fx, entries = [] }) => {
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const setLockedIds = fx?.setLockedIds;
  const ids = lockedIdsOf(entries);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const key = ids.join(',');

  useEffect(() => {
    setLockedIds?.(idsRef.current);
  }, [setLockedIds, key]);

  const setCardVibes = fx?.setCardVibes;
  const vibes = entries.map((e) => ({ entryId: e?.entryId, boostedVibeId: e?.boostedVibeId ?? null }));
  const vibesRef = useRef(vibes);
  vibesRef.current = vibes;
  const vibesKey = vibes.map((v) => `${v.entryId}:${v.boostedVibeId ?? ''}`).join(',');

  useEffect(() => {
    setCardVibes?.(vibesRef.current);
  }, [setCardVibes, vibesKey]);

  const onSongStarted = useCallback((frame) => {
    if (frame?.entryId == null) return;
    const f = readyOf(fxRef);
    f?.clearCardVibe?.(frame.entryId);
    const card = f?.getCard(frame.entryId);
    if (!card) return;
    card.clearAfterglow();
    card.clearBuild();
  }, []);

  return { onSongStarted };
};

export default useFxAfterglow;
